import { l as DocContext, u as useRecordVisibilityChanges, o as Context, k as jsxRuntimeExports, g as getMediaLicenseDisplay } from "./doenetml-a_KitGQm.js";
import React__default, { useContext, useRef, useMemo } from "react";
import { j as jxg } from "./JSXGraphRenderer-BMkZb_8Q.js";
import { BoardContext, IMAGE_LAYER_OFFSET } from "./graph-gLVvVe_r.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-Ca2HvWV4.js";
import { s as sizeToCSS } from "./css-BJLDLYRJ.js";
import { D as DescriptionPopover, a as DescriptionAsDetails } from "./Description-C7malmvR.js";
import { g as getNonInlineMediaLayoutStyles } from "./nonInlineMediaLayout-CkXHaqzo.js";
import { N as NonInlineMediaWrapper } from "./NonInlineMediaWrapper-Djk_oD9M.js";
import { u as usePointerDragState, a as useBoardPointerTracking } from "./useBoardPointerTracking-DV6a8Wvg.js";
import { u as useDraggableRefs, a as useJSXGraphCleanup } from "./useJSXGraphCleanup-CX91oVW8.js";
import { d as detachAnchoredGraphElement, a as attachAnchoredGraphDragHandlers } from "./useAnchoredGraphDragHandler-BKPDW5E6.js";
const image = React__default.memo(function Image(props) {
  let { componentIdx, id, SVs, children, actions, callAction } = useDoenetRenderer(props, false);
  Image.ignoreActionsWithoutCore = () => true;
  const { doenetImagesUrl } = useContext(DocContext) || {};
  let imageJXG = useRef(null);
  let anchorPointJXG = useRef(null);
  let lastUrl = useRef("");
  const board = useContext(BoardContext);
  const pointerState = usePointerDragState();
  let pointAtDown = useRef(null);
  let calculatedX = useRef(null);
  let calculatedY = useRef(null);
  let previousPositionFromAnchor = useRef(null);
  let currentSize = useRef([0, 0]);
  let currentOffset = useRef([0, 0]);
  let rotationTransform = useRef(null);
  let lastRotate = useRef(SVs.rotate);
  const { fixed, fixLocation, lastPositionFromCore } = useDraggableRefs(SVs, null);
  useBoardPointerTracking(board, pointerState);
  useJSXGraphCleanup({
    objectRef: imageJXG,
    destroy: () => detachAnchoredGraphElement(imageJXG, board)
  });
  const url = useMemo(
    () => getUrlForImage({
      source: SVs.source,
      imageId: SVs.imageId,
      doenetImagesUrl
    }),
    [SVs.source, SVs.imageId, doenetImagesUrl]
  );
  const ref = useRef(null);
  useRecordVisibilityChanges(ref, callAction, actions);
  function createImageJXG() {
    if (board === null) {
      return null;
    }
    let jsxImageAttributes = {
      visible: !SVs.hidden,
      fixed: fixed.current,
      layer: 10 * SVs.layer + IMAGE_LAYER_OFFSET,
      highlight: !fixLocation.current
    };
    let newAnchorPointJXG;
    try {
      let anchor = Context.fromAst(SVs.anchor);
      let anchorCoords = [
        anchor.get_component(0).evaluate_to_constant() ?? NaN,
        anchor.get_component(1).evaluate_to_constant() ?? NaN
      ];
      if (!Number.isFinite(anchorCoords[0])) {
        anchorCoords[0] = 0;
        jsxImageAttributes["visible"] = false;
      }
      if (!Number.isFinite(anchorCoords[1])) {
        anchorCoords[1] = 0;
        jsxImageAttributes["visible"] = false;
      }
      newAnchorPointJXG = board.create("point", anchorCoords, {
        visible: false
      });
    } catch (e) {
      jsxImageAttributes["visible"] = false;
      newAnchorPointJXG = board.create("point", [0, 0], {
        visible: false
      });
    }
    jsxImageAttributes.anchor = newAnchorPointJXG;
    let width = SVs.widthForGraph?.size || 1;
    let height = width / (SVs.aspectRatio || 1);
    if (!(Number.isFinite(width) && Number.isFinite(height))) {
      width = 0;
      height = 0;
    }
    let offset;
    if (SVs.positionFromAnchor === "center") {
      offset = [-width / 2, -height / 2];
    } else if (SVs.positionFromAnchor === "lowerleft") {
      offset = [-width, -height];
    } else if (SVs.positionFromAnchor === "lowerright") {
      offset = [0, -height];
    } else if (SVs.positionFromAnchor === "upperleft") {
      offset = [-width, 0];
    } else if (SVs.positionFromAnchor === "upperright") {
      offset = [0, 0];
    } else if (SVs.positionFromAnchor === "bottom") {
      offset = [-width / 2, -height];
    } else if (SVs.positionFromAnchor === "top") {
      offset = [-width / 2, 0];
    } else if (SVs.positionFromAnchor === "right") {
      offset = [0, -height / 2];
    } else {
      offset = [-width, -height / 2];
    }
    currentOffset.current = offset;
    let newImageJXG = board.create(
      "image",
      [url, offset, [width, height]],
      jsxImageAttributes
    );
    var tOff = board.create(
      "transform",
      [
        function() {
          return -newImageJXG.X() - newImageJXG.W() * 0.5;
        },
        function() {
          return -newImageJXG.Y() - newImageJXG.H() * 0.5;
        }
      ],
      { type: "translate" }
    );
    var tOffInverse = board.create(
      "transform",
      [
        function() {
          return newImageJXG.X() + newImageJXG.W() * 0.5;
        },
        function() {
          return newImageJXG.Y() + newImageJXG.H() * 0.5;
        }
      ],
      { type: "translate" }
    );
    var tRot = board.create("transform", [SVs.rotate], {
      type: "rotate"
    });
    tOff.bindTo(newImageJXG);
    tRot.bindTo(newImageJXG);
    tOffInverse.bindTo(newImageJXG);
    rotationTransform.current = tRot;
    lastRotate.current = SVs.rotate;
    attachAnchoredGraphDragHandlers({
      board,
      newJXG: newImageJXG,
      newAnchorPoint: newAnchorPointJXG,
      pointerState,
      pointAtDown,
      calculatedX,
      calculatedY,
      fixed,
      fixLocation,
      lastPositionFromCore,
      componentIdx,
      actions,
      callAction,
      actionNames: {
        move: "moveImage",
        focused: "imageFocused",
        clicked: "imageClicked"
      },
      imageMode: {
        getCurrentSize: () => currentSize.current,
        getCurrentOffset: () => currentOffset.current
      }
    });
    imageJXG.current = newImageJXG;
    anchorPointJXG.current = newAnchorPointJXG;
    lastUrl.current = url;
    previousPositionFromAnchor.current = SVs.positionFromAnchor;
    currentSize.current = [width, height];
    imageJXG.current.fullUpdate();
  }
  if (board) {
    let anchorCoords;
    try {
      let anchor = Context.fromAst(SVs.anchor);
      anchorCoords = [
        anchor.get_component(0).evaluate_to_constant() ?? NaN,
        anchor.get_component(1).evaluate_to_constant() ?? NaN
      ];
    } catch (e) {
      anchorCoords = [NaN, NaN];
    }
    lastPositionFromCore.current = anchorCoords;
    if (imageJXG.current === null) {
      if (url) {
        createImageJXG();
      }
    } else if (url !== lastUrl.current) {
      detachAnchoredGraphElement(imageJXG, board);
      if (anchorPointJXG.current) {
        board.removeObject(anchorPointJXG.current);
        anchorPointJXG.current = null;
      }
      lastUrl.current = url;
      if (url) {
        createImageJXG();
      }
    } else {
      anchorPointJXG.current?.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        anchorCoords
      );
      let visible = !SVs.hidden;
      if (Number.isFinite(anchorCoords[0]) && Number.isFinite(anchorCoords[1])) {
        let actuallyChangedVisibility = imageJXG.current.visProp["visible"] !== visible;
        imageJXG.current.visProp["visible"] = visible;
        imageJXG.current.visPropCalc["visible"] = visible;
        if (actuallyChangedVisibility) {
          imageJXG.current.setAttribute({ visible });
        }
      } else {
        imageJXG.current.visProp["visible"] = false;
        imageJXG.current.visPropCalc["visible"] = false;
      }
      let layer = 10 * SVs.layer + IMAGE_LAYER_OFFSET;
      let layerChanged = imageJXG.current.visProp.layer !== layer;
      if (layerChanged) {
        imageJXG.current.setAttribute({ layer });
      }
      imageJXG.current.visProp.highlight = !fixLocation.current;
      imageJXG.current.visProp.fixed = fixed.current;
      imageJXG.current.isDraggable = !fixLocation.current;
      imageJXG.current.needsUpdate = true;
      let width = SVs.widthForGraph?.size || 1;
      let height = width / (SVs.aspectRatio || 1);
      if (!(Number.isFinite(width) && Number.isFinite(height))) {
        width = 0;
        height = 0;
      }
      let sizeChanged = width !== currentSize.current[0] || height !== currentSize.current[1];
      if (sizeChanged) {
        imageJXG.current.setSize(width, height);
        currentSize.current = [width, height];
      }
      if (SVs.rotate != lastRotate.current) {
        rotationTransform.current?.setMatrix(board, "rotate", [
          SVs.rotate
        ]);
        lastRotate.current = SVs.rotate;
      }
      if (SVs.positionFromAnchor !== previousPositionFromAnchor.current || sizeChanged) {
        let offset;
        if (SVs.positionFromAnchor === "center") {
          offset = [-width / 2, -height / 2];
        } else if (SVs.positionFromAnchor === "lowerleft") {
          offset = [-width, -height];
        } else if (SVs.positionFromAnchor === "lowerright") {
          offset = [0, -height];
        } else if (SVs.positionFromAnchor === "upperleft") {
          offset = [-width, 0];
        } else if (SVs.positionFromAnchor === "upperright") {
          offset = [0, 0];
        } else if (SVs.positionFromAnchor === "bottom") {
          offset = [-width / 2, -height];
        } else if (SVs.positionFromAnchor === "top") {
          offset = [-width / 2, 0];
        } else if (SVs.positionFromAnchor === "right") {
          offset = [0, -height / 2];
        } else {
          offset = [-width, -height / 2];
        }
        imageJXG.current.relativeCoords.setCoordinates(
          jxg.COORDS_BY_USER,
          offset
        );
        previousPositionFromAnchor.current = SVs.positionFromAnchor;
        currentOffset.current = offset;
        imageJXG.current.fullUpdate();
      } else {
        imageJXG.current.relativeCoords.setCoordinates(
          jxg.COORDS_BY_USER,
          currentOffset.current
        );
        imageJXG.current.update();
      }
      if (anchorPointJXG.current) {
        anchorPointJXG.current.needsUpdate = true;
        anchorPointJXG.current.update();
      }
      board.updateRenderer();
    }
    return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id });
  }
  if (SVs.hidden) return null;
  let outerStyle = {};
  let innerStyle = {};
  let mediaContainerStyle = {};
  let mediaColumnStyle = {};
  if (SVs.displayMode === "inline") {
    outerStyle = {
      display: "inline-block",
      verticalAlign: "middle",
      margin: "12px 0"
    };
    innerStyle = {
      display: "inline-flex",
      alignItems: "start"
    };
  } else {
    ({ outerStyle, innerStyle, mediaContainerStyle, mediaColumnStyle } = getNonInlineMediaLayoutStyles({
      horizontalAlign: SVs.horizontalAlign,
      mediaWidth: sizeToCSS(SVs.width)
    }));
  }
  let imageStyle = {
    maxWidth: "100%",
    width: sizeToCSS(SVs.width)
  };
  if ((SVs.aspectRatio ?? 0) > 0) {
    imageStyle.aspectRatio = String(SVs.aspectRatio);
  }
  if (!url) {
    imageStyle.border = "var(--mainBorder)";
  }
  const shortDescription = SVs.decorative ? "" : SVs.shortDescription || void 0;
  const descriptionChild = children.find((child) => child);
  const attribution = renderImageAttribution({
    idPrefix: id,
    imageName: SVs.imageName,
    authorName: SVs.authorName,
    authorUrl: SVs.authorUrl,
    originalUrl: SVs.originalUrl,
    licenseCodes: SVs.licenseCodes,
    licenseVersion: SVs.licenseVersion,
    licenseNames: SVs.licenseNames,
    licenseUrls: SVs.licenseUrls
  });
  let descriptionId = void 0;
  let description = null;
  if (descriptionChild || attribution) {
    descriptionId = `${id}-description-content`;
    const descriptionContent = /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { id: descriptionId, children: [
      descriptionChild,
      attribution
    ] });
    description = SVs.displayMode === "inline" ? /* @__PURE__ */ jsxRuntimeExports.jsx(DescriptionPopover, { children: descriptionContent }) : /* @__PURE__ */ jsxRuntimeExports.jsx(DescriptionAsDetails, { children: descriptionContent });
  }
  const media = url ? /* @__PURE__ */ jsxRuntimeExports.jsx(
    "img",
    {
      id,
      src: url,
      style: imageStyle,
      alt: shortDescription,
      "aria-details": descriptionId
    }
  ) : /* @__PURE__ */ jsxRuntimeExports.jsx("div", { id, style: imageStyle, children: SVs.shortDescription });
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    NonInlineMediaWrapper,
    {
      id,
      displayMode: SVs.displayMode,
      suppressTopMargin: Boolean(SVs.renderInlineForListItem),
      layoutStyles: {
        outerStyle,
        innerStyle,
        mediaContainerStyle,
        mediaColumnStyle
      },
      media,
      description,
      containerRef: ref
    }
  );
});
function isSafeHref(url) {
  const stripped = url.replace(/[\u0000-\u001f\u007f\s]/g, "").toLowerCase();
  const schemeMatch = stripped.match(/^([a-z][a-z0-9+.-]*):/);
  if (!schemeMatch) {
    return true;
  }
  const scheme = schemeMatch[1];
  return scheme === "http" || scheme === "https" || scheme === "mailto";
}
function maybeLink(text, url, key) {
  return url && isSafeHref(url) ? /* @__PURE__ */ jsxRuntimeExports.jsx("a", { href: url, target: "_blank", rel: "noopener noreferrer", children: text }, key) : /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: text }, key);
}
function renderImageAttribution({
  idPrefix,
  imageName,
  authorName,
  authorUrl,
  originalUrl,
  licenseCodes,
  licenseVersion,
  licenseNames,
  licenseUrls
}) {
  let licenses;
  if (licenseCodes && licenseCodes.length > 0) {
    licenses = [];
    for (const code of licenseCodes) {
      const display = getMediaLicenseDisplay(
        code,
        licenseVersion
      );
      if (display) {
        licenses.push({
          kind: display.kind,
          label: display.label,
          url: display.url
        });
      }
    }
  } else {
    const names = licenseNames ?? [];
    const urls = licenseUrls ?? [];
    licenses = names.map((name, i) => ({
      kind: "generic",
      label: name,
      url: urls[i] ?? null
    }));
  }
  const hasAuthor = Boolean(authorName);
  const hasLicense = licenses.length > 0;
  if (!hasAuthor && !hasLicense) {
    return null;
  }
  const subjectText = imageName ? `“${imageName}”` : "Image";
  const subjectNode = maybeLink(subjectText, originalUrl);
  const subjectAndAuthor = hasAuthor ? /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
    subjectNode,
    " by ",
    maybeLink(authorName, authorUrl)
  ] }) : subjectNode;
  function joinWithOr(nodes) {
    return nodes.map(
      (node, i) => i === 0 ? /* @__PURE__ */ jsxRuntimeExports.jsx(React__default.Fragment, { children: node }, i) : /* @__PURE__ */ jsxRuntimeExports.jsxs(React__default.Fragment, { children: [
        " or ",
        node
      ] }, i)
    );
  }
  let sentence;
  if (!hasLicense) {
    sentence = /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
      subjectAndAuthor,
      "."
    ] });
  } else if (licenses.every((l) => l.kind === "public-domain")) {
    const links = joinWithOr(
      licenses.map((l, i) => maybeLink(l.label, l.url, i))
    );
    sentence = /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
      subjectAndAuthor,
      " is in the public domain (",
      links,
      ")."
    ] });
  } else {
    const clauses = licenses.map((l, i) => {
      const link = maybeLink(l.label, l.url, i);
      if (l.kind === "creative-commons") {
        return /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
          "a ",
          link,
          " license"
        ] });
      } else if (l.kind === "generic") {
        return /* @__PURE__ */ jsxRuntimeExports.jsx(jsxRuntimeExports.Fragment, { children: link });
      } else {
        return /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
          "the ",
          link
        ] });
      }
    });
    sentence = /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
      subjectAndAuthor,
      " is licensed under ",
      joinWithOr(clauses),
      "."
    ] });
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx("p", { id: `${idPrefix}-attribution`, className: "image-attribution", children: sentence });
}
function getUrlForImage({
  source,
  imageId,
  doenetImagesUrl = "https://doenet.org/api/media"
}) {
  if (imageId) {
    const separator = doenetImagesUrl.endsWith("/") ? "" : "/";
    return doenetImagesUrl + separator + imageId;
  } else if (/^\s*doenet:/i.test(source)) {
    return "";
  } else {
    return source;
  }
}
export {
  image as default
};
